import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { serviceTypesAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import DeleteButton from '../components/ui/DeleteButton';
import { Plus, Pencil, Trash2, ArrowLeft, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

import { canAdmin } from '../utils/permissions';

const ServiceTypeManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [nameDraft, setNameDraft] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const isAdmin = canAdmin(user);

  useEffect(() => { loadTypes(); }, []);

  const loadTypes = async () => {
    try {
      const res = await serviceTypesAPI.getAll();
      setTypes(res.data);
    } catch (error) {
      toast.error('Failed to load service types');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t.service_type_id);
    setNameDraft(t.name);
  };

  const saveEdit = async (t) => {
    if (!nameDraft.trim()) return;
    try {
      await serviceTypesAPI.update(t.service_type_id, { name: nameDraft.trim() });
      toast.success('Service type renamed');
      setEditingId(null);
      loadTypes();
    } catch (error) {
      toast.error('Failed to rename');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await serviceTypesAPI.create({ name: newName.trim() });
      toast.success('Service type added');
      setIsAddOpen(false);
      setNewName('');
      loadTypes();
    } catch (error) {
      toast.error('Failed to add service type');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service type?')) return;
    try {
      await serviceTypesAPI.delete(id);
      toast.success('Service type deleted');
      loadTypes();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-50">
        <Navbar />
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-8">
        {/* Header */}
        <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-8">
          <button
            onClick={() => navigate('/services')}
            data-testid="back-to-services-btn"
            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors mt-0.5"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900">Service Types</h1>
            <p className="mt-1 text-sm sm:text-base text-slate-600">Manage service types</p>
          </div>
        </div>

        {isAdmin && (
          <div className="mb-5 sm:mb-6">
            <button
              onClick={() => setIsAddOpen(true)}
              data-testid="add-service-type-btn"
              className="h-9 sm:h-10 px-4 sm:px-6 rounded-full bg-green-700 text-white hover:bg-green-800 transition-all duration-200 shadow-sm font-medium flex items-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Service Type
            </button>
          </div>
        )}

        {/* Types List */}
        {types.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
            <p className="text-sm text-slate-500 mb-3">No service types yet.</p>
            {isAdmin && (
              <button onClick={() => setIsAddOpen(true)} className="text-sm text-green-700 hover:text-green-800 font-medium">
                Add your first service type
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="divide-y divide-slate-100">
              {types.map((t) => (
                <div
                  key={t.service_type_id}
                  data-testid={`service-type-item-${t.service_type_id}`}
                  className="flex items-center justify-between px-4 sm:px-5 py-3.5 hover:bg-slate-50 transition-colors"
                >
                  {editingId === t.service_type_id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        data-testid="service-type-edit-input"
                        className="h-8 text-sm flex-1"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(t); if (e.key === 'Escape') setEditingId(null); }}
                      />
                      <button onClick={() => saveEdit(t)} data-testid="save-service-type-btn" className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded transition-colors">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditingId(null)} data-testid="cancel-service-type-btn" className="p-1.5 text-slate-400 hover:bg-slate-100 rounded transition-colors">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm font-medium text-slate-800">{t.name}</span>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEdit(t)}
                            data-testid={`rename-service-type-${t.service_type_id}`}
                            className="p-1.5 text-slate-400 hover:text-green-700 hover:bg-slate-100 rounded transition-colors"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <DeleteButton
                            onConfirm={() => handleDelete(t.service_type_id)}
                            className="p-1.5 rounded"
                            iconSize="w-3.5 h-3.5"
                            title="Delete service type"
                            testId={`delete-service-type-${t.service_type_id}`}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Service Type Dialog */}
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogContent className="bg-white mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>Add Service Type</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <Label htmlFor="new-type-name">Type Name</Label>
                <Input
                  id="new-type-name"
                  data-testid="new-service-type-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g., Sunday Morning"
                  required
                />
              </div>
              <Button type="submit" data-testid="submit-service-type-btn" className="w-full">Add Service Type</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ServiceTypeManagement;
